import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { userServices } from '../../services/userServices';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../services/authentication/authContext';
import { Button, Typography, IconButton, Tooltip, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, CircularProgress, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import TableSkeleton from '../common/TableSkeleton';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import { Edit, Delete, Add } from '@mui/icons-material';
import PageHeader from '../ui/PageHeader';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import KeyIcon from '@mui/icons-material/VpnKey';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SectionCard from '../ui/SectionCard';
import { availableRoles } from '../../constants/roles';
import { jwtDecode } from 'jwt-decode';
import '../../css/Button.css';
import '../../css/DataTable.css';
import '../../css/Dialog.css';
import ConfirmationDialog from '../common/ConfirmationDialog';
import { useSnackbar } from '../../utils/useSnackbar';

const Users = () => {
  const [usersData, setUsersData] = useState([]);
  const [roles, setRoles] = useState([]);
  const isAdmin = Array.isArray(roles) && (roles.includes('ADMIN') || roles.includes('SUPER_ADMIN'));
  const [loading, setLoading] = useState(true);
  const pageContainerRef = useRef(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const navigationUrl = "/home/user";
  const [searchUser, setSearchUser] = useState('');
  const [debouncedSearchUser, setDebouncedSearchUser] = useState(searchUser);
  const [columnFilter, setColumnFilter] = useState('');
  const [debouncedFilterUser, setDebouncedFilterUser] = useState(columnFilter);
  const [sortModel, setSortModel] = useState([{ field: 'id', sort: 'desc' }]);
  const [openDialog, setOpenDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const pageSizeOptions = [5, 10, 15];
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [loggedOutData, setLoggedOutData] = useState([]);
  const [inactiveLoading, setInactiveLoading] = useState(true);
  const [showInactiveUsers, setShowInactiveUsers] = useState(false);
  const theme = useTheme();
  const isTabletOrBelow = useMediaQuery(theme.breakpoints.down('lg'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSearchFirstRender = useRef(true);
  const isFilterFirstRender = useRef(true);
  const isSortFirstRender = useRef(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const sortField = sortModel[0]?.field || "id";
      const sortDir = sortModel[0]?.sort || "desc";

      const response = await userServices.getAllUser(debouncedSearchUser, debouncedFilterUser, paginationModel.page, paginationModel.pageSize, sortField, sortDir);
      const transformedUsers = (response.users || []).map(u => ({
        ...u.user,
        yesterdayWorkedTime: u.YesterdayWorkedTime || '00h:00m',
      }));
      setUsersData(transformedUsers);
      setRowCount(response.totalElements);

      const transformedLoggedOut = (response.loggedOutUsers || []).map(u => ({
        ...u.user,
        lastStopTime: u.lastStopTime || '00h:00m',
      }));
      const nonAdminLoggedOut = transformedLoggedOut.filter(user =>
        !(Array.isArray(user.roles) && user.roles.includes('ADMIN'))
      );
      // setLoggedOutData(nonAdminLoggedOut);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setUsersData([]);
        setRowCount(0);
      } else {
        console.error("Error fetching data", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLoggedOutUsers = async () => {
    setInactiveLoading(true);
    try {
      const response = await userServices.getAllLoggedOutUsers();
      const loggedOutUsers = (response || []).map(u => ({
        id: u.id,
        firstName: u.firstName,
        lastName: u.lastName,
        designation: u.designation,
        department: u.department,
        lastStopTime: u.lastStopTime || '-',
      }));
      setLoggedOutData(loggedOutUsers);
    } catch (error) {
      console.error("Error fetching logged-out users", error);
    } finally {
      setInactiveLoading(false);
    }
  };

  // Decode roles separately — only when accessToken changes
  useEffect(() => {
    if (accessToken) {
      const decodedToken = jwtDecode(accessToken);
      let extractedRoles = [];
      if (Array.isArray(decodedToken.roles)) {
        extractedRoles = decodedToken.roles;
      } else if (typeof decodedToken.roles === "string") {
        extractedRoles = decodedToken.roles.split(",").map(role => role.trim());
      }
      setRoles(extractedRoles);
    }
  }, [accessToken]);

  // Fetch data when pagination, search, filter, or sort changes
  useEffect(() => {
    if (accessToken) {
      fetchData(paginationModel.page, paginationModel.pageSize, debouncedSearchUser, debouncedFilterUser);
    }
  }, [accessToken, paginationModel, debouncedSearchUser, debouncedFilterUser, sortModel]);

  useEffect(() => {
    if (accessToken) {
      fetchLoggedOutUsers();
    }
  }, [accessToken]);

  const handlePaginationModelChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
  };

  useEffect(() => {
    const message = localStorage.getItem("successMessage");
    if (message) {
      showSnackbar(message, 'success');
      localStorage.removeItem("successMessage");
    }
  }, []);

  const handleAddUser = () => {
    if (accessToken) {
      navigate(navigationUrl);
    }
  };

  const handleEditUser = (user) => {
    if (accessToken) {
      navigate(navigationUrl, { state: { userData: user } });
    }
  };

  const handleDeleteUser = (userId) => {
    setUserToDelete(userId);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setUserToDelete(null);
  };

  const confirmDelete = () => {
    if (userToDelete) {
      userServices.deleteUserData(userToDelete)
        .then(() => {
          setUsersData((prevData) => prevData.filter(user => user.id !== userToDelete));
          showSnackbar('User deleted successfully!', 'success');
        })
        .catch((error) => {
          showSnackbar('Error deleting user data', 'error');
        });
    }
    handleCloseDialog();
  };

  const handleResetPasswordDialog = (userId) => {
    setUserToReset(userId);
    setOpenResetDialog(true);
  };

  const handleCloseResetDialog = () => {
    setOpenResetDialog(false);
    setUserToReset(null);
  };

  const confirmResetPassword = () => {
    if (userToReset) {
      const type = "reset";

      const formData = {
        id: userToReset,
        type
      };

      userServices.resetPasswordById(formData)
        .then(() => {
          showSnackbar('Password reset successfully!', 'success');
        })
        .catch((error) => {
          showSnackbar('Error resetting password', 'error');
        });
    }
    handleCloseResetDialog();
  };

  useEffect(() => {
    if (isSearchFirstRender.current) {
      isSearchFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const value = searchUser.trim();

      if (value === "" || value.length > 2) {
        setDebouncedSearchUser(value);
        setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchUser]);

  useEffect(() => {
    if (isFilterFirstRender.current) {
      isFilterFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const value = columnFilter.trim();

      if (value === "" || value.length > 2) {
        setDebouncedFilterUser(value);
        setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [columnFilter]);

  const columns = useMemo(() => [
    {
      field: 'firstName', headerName: t('user.firstName'), flex: 1, minWidth: isMobile ? 120 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value}</span></Tooltip>
      )
    },
    {
      field: 'lastName', headerName: t('user.lastName'), flex: 1, minWidth: isMobile ? 120 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value}</span></Tooltip>
      )
    },
    {
      field: 'emailId', headerName: t('user.emailId'), flex: 1, minWidth: isMobile ? 200 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value}</span></Tooltip>
      )
    },
    {
      field: 'mobileNo', headerName: t('user.mobileNo'), flex: 1, minWidth: isMobile ? 120 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value}</span></Tooltip>
      )
    },
    {
      field: 'designation', headerName: t('user.designation'), flex: 1, minWidth: isMobile ? 150 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value}</span></Tooltip>
      )
    },
    {
      field: 'department', headerName: t('user.department'), flex: 1, minWidth: isMobile ? 150 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value}</span></Tooltip>
      )
    },
    {
      field: 'roles',
      headerName: t('user.roles'),
      flex: 2,
      minWidth: isMobile ? 200 : 0,
      renderCell: (params) => {
        const roleNames = params.row.roles.map(role => availableRoles[role] || role).join(", ");
        return <Tooltip title={roleNames}><span>{roleNames}</span></Tooltip>;
      }
    },
    ...(isAdmin ? [
      {
        field: 'yesterdayWorkedTime',
        headerName: 'Worked Time',
        flex: 1,
        minWidth: isMobile ? 120 : 0,
        renderCell: (params) => (
          <Tooltip title={params.value || ''}>
            <span>{params.value || '00h:00m'}</span>
          </Tooltip>
        ),
      },
      {
        field: 'actions',
        headerName: t('user.actions'),
        width: 150,
        minWidth: isMobile ? 150 : 0,
        renderCell: (params) => (
          <div>
            <React.Fragment>
              <IconButton color="primary" onClick={() => handleEditUser(params.row)} className="icon-button">
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton color="secondary" onClick={() => handleDeleteUser(params.row.id)} className="icon-button">
                <DeleteIcon fontSize="small" />
              </IconButton>
              <IconButton
                color="default"
                onClick={() => handleResetPasswordDialog(params.row.id)}
                className="icon-button"
              >
                <KeyIcon fontSize="small" />
              </IconButton>
            </React.Fragment>
          </div>
        ),
      }
    ] : []),
  ], [isAdmin, t, isMobile, availableRoles]);

  const filteredDataMemo = useMemo(() => {
    if (!usersData) return [];

    const filter = (user, search) => {
      const lowerSearch = search?.trim().toLowerCase() || '';
      return (
        user?.firstName?.toLowerCase().includes(lowerSearch) ||
        user?.lastName?.toLowerCase().includes(lowerSearch) ||
        user?.emailId?.toLowerCase().includes(lowerSearch) ||
        user?.mobileNo?.toLowerCase().includes(lowerSearch) ||
        user?.designation?.toLowerCase().includes(lowerSearch) ||
        user?.department?.toLowerCase().includes(lowerSearch) ||
        user?.roles?.join(", ").toLowerCase().includes(lowerSearch)
      );
    };

    return usersData
      .filter(user => filter(user, searchUser) && filter(user, columnFilter));
  }, [usersData, searchUser, columnFilter]);

  const usersColumnVisibilityModel = useMemo(() => {
    if (isMobile) {
      return {
        // mobileNo: false,
        // designation: false,
        // department: false,
        // roles: false,
      };
    }

    if (isTabletOrBelow) {
      return {
        // mobileNo: false,
        // department: false,
      };
    }

    return {};
  }, [isMobile, isTabletOrBelow]);

  return (
    <div ref={pageContainerRef} className="data-table-container">
      <PageHeader title="Users" />

      {/* Snackbar */}
      {SnackbarComponent}

      {isAdmin && loggedOutData.length > 0 && (
        <SectionCard
          title="Logged Out Users"
          badge={loggedOutData.length.toString()}
          action={
            <Button
              size="small"
              variant="outlined"
              onClick={() => setShowInactiveUsers(!showInactiveUsers)}
              startIcon={showInactiveUsers ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                borderRadius: 999,
                textTransform: 'none',
                px: 2,
                py: 0.5,
                fontSize: '0.75rem',
                fontWeight: 600,
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.dark',
                  backgroundColor: 'rgba(4, 88, 100, 0.08)',
                }
              }}
            >
              {showInactiveUsers ? 'Hide' : 'Show'}
            </Button>
          }
          sx={{ mb: 3, borderRadius: 1 }}
        >
          {showInactiveUsers && (
            <TableContainer sx={{
              maxHeight: '25rem',
              overflow: 'auto',
              mt: 1,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Table size="small" aria-label="inactive users table" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', color: 'text.primary' }}>First Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', color: 'text.primary' }}>Last Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', color: 'text.primary' }}>Designation</TableCell>
                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', color: 'text.primary' }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', color: 'text.primary' }}>Last Logout Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inactiveLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: "center", py: 4 }}>
                        <CircularProgress size={24} sx={{ color: 'primary.main' }} />
                        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                          Fetching data...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (loggedOutData.length > 0 ? loggedOutData.map((user) => (
                    <TableRow key={user.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell sx={{ py: 1.5 }}>{user?.firstName}</TableCell>
                      <TableCell sx={{ py: 1.5 }}>{user?.lastName}</TableCell>
                      <TableCell sx={{ py: 1.5 }}>{user?.designation}</TableCell>
                      <TableCell sx={{ py: 1.5 }}>{user?.department}</TableCell>
                      <TableCell sx={{ py: 1.5 }}>{user?.lastStopTime || '-'}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: "center", py: 3, color: 'text.secondary' }}>
                        No inactive users at the moment.
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </SectionCard>
      )}

      <div className="search-and-button">
        <Button
          variant="contained"
          onClick={handleAddUser}
          className="add-button"
          startIcon={<AddIcon />}
        >
          {t('user.add')}
        </Button>

        {/* General Search Filter */}
        <div className="search-container">
          <input
            type="text"
            placeholder="Type to search..."
            value={searchUser}
            onChange={(e) => setSearchUser(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="data-table">
        <div className="data-grid-container" style={{ height: '70vh' }}>
          {loading ? (
            <Box sx={{ height: '100%', overflow: 'hidden' }}>
              <TableSkeleton rows={8} columns={6} />
            </Box>
          ) : (
            <DataGrid
              loading={false}
              rows={filteredDataMemo}
              columns={columns}
              pagination
              paginationMode="server"
              filterMode="server"
              onFilterModelChange={(newFilterModel) => {
                const value = newFilterModel.items?.[0]?.value || '';
                setColumnFilter((prev) => (prev === value ? prev : value));
              }}
              sortingMode="server"
              onSortModelChange={(newModel) => {
                if (isSortFirstRender.current) {
                  isSortFirstRender.current = false;
                  if (!newModel?.length) return;
                }
                const next = newModel?.[0];
                const current = sortModel?.[0];
                if (next?.field === current?.field && next?.sort === current?.sort) return;
                if (!newModel?.length && sortModel.length > 0) {
                  const currentField = sortModel[0].field;
                  const isColumnDefined = columns.some(c => c.field === currentField);

                  if (!isColumnDefined) {
                    return;
                  }
                }

                setSortModel(newModel);
                setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
              }}
              sortModel={sortModel}
              paginationModel={paginationModel}
              rowCount={rowCount}
              pageSizeOptions={pageSizeOptions}
              onPaginationModelChange={handlePaginationModelChange}
              disableSelectionOnClick
              columnVisibilityModel={usersColumnVisibilityModel}
            />
          )}
        </div>

        <ConfirmationDialog
          open={openDialog}
          onClose={handleCloseDialog}
          onConfirm={confirmDelete}
          title="Delete Confirmation"
          message="Are you sure you want to delete this user?"
          messageSecond="*Associated Clients, Jobs and Tasks with this user are also be deleted."
          cancelLabel="Cancel"
          confirmLabel="Delete"
          isDelete={true}
        />

        <ConfirmationDialog
          open={openResetDialog}
          onClose={handleCloseResetDialog}
          onConfirm={confirmResetPassword}
          title="Reset Password Confirmation"
          message="Are you sure you want to reset the password?"
          cancelLabel="Cancel"
          confirmLabel="Reset"
          confirmColor={'primary'}
        />

      </div>
    </div>
  );
};

export default Users;
